import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Check } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { FadeUp } from "./Reveal";
import { SERVICE_DETAILS, SERVICES } from "@/data";

const SITE = "https://cretexa.ca";

function useServiceSeo(slug, s) {
  useEffect(() => {
    if (!s) return;
    const prevTitle = document.title;
    document.title = s.metaTitle;

    const created = [];
    const setMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created.push(el);
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      return () => prev !== null && el.setAttribute("content", prev);
    };

    const restorers = [
      setMeta("name", "description", s.metaDescription),
      setMeta("property", "og:title", s.metaTitle),
      setMeta("property", "og:description", s.metaDescription),
      setMeta("property", "og:url", `${SITE}/services/${slug}`),
    ];

    let canonical = document.head.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute("href") : null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
      created.push(canonical);
    }
    canonical.setAttribute("href", `${SITE}/services/${slug}`);

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      name: s.title,
      serviceType: s.title,
      description: s.metaDescription,
      url: `${SITE}/services/${slug}`,
      areaServed: { "@type": "AdministrativeArea", name: "Alberta, Canada" },
      provider: { "@type": "GeneralContractor", name: "Cretexa", url: SITE },
    });
    document.head.appendChild(ld);
    created.push(ld);

    return () => {
      document.title = prevTitle;
      restorers.forEach((r) => r && r());
      if (prevCanonical) canonical.setAttribute("href", prevCanonical);
      created.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    };
  }, [slug, s]);
}

export default function ServicePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const s = SERVICE_DETAILS[slug];

  useServiceSeo(slug, s);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!s) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] grain flex flex-col items-center justify-center text-center px-6" data-testid="service-not-found">
        <p className="font-display font-black text-6xl text-white mb-6">404</p>
        <p className="font-mono text-[#A3A3A3] mb-8">This service could not be found.</p>
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 border border-[#FF4D00] text-[#FF4D00] px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#FF4D00] hover:text-black transition-colors"
        >
          <ArrowLeft size={16} /> Back Home
        </button>
      </div>
    );
  }

  const others = SERVICES.filter((x) => x.slug !== slug);

  return (
    <div className="App grain bg-[#0A0A0A]" data-testid={`service-page-${slug}`}>
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-end overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={s.image} alt={`${s.title} — Cretexa, Calgary Alberta`} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-black/50" />
        </div>
        <div className="relative z-10 w-full mx-auto max-w-[1600px] px-6 lg:px-12 pb-16 lg:pb-24 pt-36">
          <nav className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#A3A3A3] mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#FF4D00] transition-colors">Home</Link>
            <span aria-hidden="true">/</span>
            <Link to="/#services" className="hover:text-[#FF4D00] transition-colors">Services</Link>
            <span aria-hidden="true">/</span>
            <span className="text-[#FF4D00]">{s.title}</span>
          </nav>
          <div className="flex items-center gap-3 mb-6">
            <span className="font-display font-black text-3xl text-[#FF4D00]" aria-hidden="true">[{s.index}]</span>
            <span className="w-8 h-px bg-[#FF4D00]" aria-hidden="true" />
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF4D00]">Service</span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-black uppercase tracking-tighter text-white leading-[0.9] text-5xl sm:text-6xl lg:text-8xl max-w-5xl"
          >
            {s.h1}
          </motion.h1>
          <p className="mt-8 font-mono font-light text-[#A3A3A3] text-lg max-w-2xl">{s.tagline}</p>
        </div>
      </section>

      {/* Intro + capabilities */}
      <section className="py-24 lg:py-32 bg-[#0A0A0A]">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <FadeUp className="lg:col-span-5">
            <h2 className="font-display font-bold uppercase tracking-tighter text-white text-3xl lg:text-5xl leading-[0.9] mb-8">
              Overview
            </h2>
            <p className="font-mono font-light text-[#A3A3A3] text-base leading-relaxed">{s.intro}</p>

            <div className="mt-12">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#555] mb-5">Sectors Served</p>
              <div className="flex flex-wrap gap-3">
                {s.sectors.map((sec) => (
                  <span key={sec} className="font-mono text-xs uppercase tracking-[0.15em] border border-[#333] text-[#A3A3A3] px-4 py-2">
                    {sec}
                  </span>
                ))}
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.1} className="lg:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#FF4D00] mb-8">Capabilities</p>
            <ul className="border-t border-[#333]">
              {s.capabilities.map((c, i) => (
                <li key={i} className="flex items-start gap-4 py-5 border-b border-[#333]" data-testid={`capability-${i}`}>
                  <Check size={20} className="text-[#FF4D00] shrink-0 mt-0.5" />
                  <span className="font-mono text-white text-base">{c}</span>
                </li>
              ))}
            </ul>
          </FadeUp>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#FF4D00] text-black">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 py-20 lg:py-28 flex flex-col lg:flex-row lg:items-center justify-between gap-10">
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl lg:text-6xl leading-[0.9] max-w-2xl">
            Need {s.title} in Alberta?
          </h2>
          <Link
            to="/#contact"
            className="group inline-flex items-center gap-3 bg-black text-white font-mono uppercase tracking-[0.15em] text-sm px-10 py-5 hover:bg-[#0A0A0A] transition-colors w-fit"
            data-testid="service-cta"
          >
            Get a Quote
            <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* Other services */}
      <section className="py-24 lg:py-32 bg-[#0A0A0A]">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#555] mb-10">Other Services</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={`/services/${o.slug}`}
                className="group border border-[#333] bg-[#111] p-6 hover:border-[#FF4D00] transition-colors flex flex-col justify-between min-h-[160px]"
                data-testid={`other-service-${o.slug}`}
              >
                <span className="font-display font-black text-2xl text-[#FF4D00]" aria-hidden="true">[{o.index}]</span>
                <span className="font-display font-bold uppercase tracking-tight text-white text-lg mt-8 group-hover:text-[#FF4D00] transition-colors flex items-center gap-2">
                  {o.title}
                  <ArrowUpRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
